import {
  IAddress,
  IArrestee,
  IArresteeForm,
  IFile,
  IMasterNamePerson,
  IUser,
} from '@/models';
import { IDriverLicense } from '@/models/driver.license';
import { AgeGroup } from '@/models/kase.involvement';
import { parsePhoneNumber } from '@/utils';
import { calculateAgeYears, parseLocalDate } from '@/utils/datetime';

// Tab identifiers
export enum ArrestEditTab {
  ADMINISTRATIVE = 'administrative',
  PERSONAL_INFO = 'personal-info',
  ARREST_INFO = 'arrest-info',
  OFFENSE_DETAILS = 'offense-details',
  ASSOCIATED_PERSONS = 'associated-persons',
  NARRATIVE = 'narrative',
  SIGNATURES_VERIFICATION = 'signatures-verification',
}

// Tab configuration type
export interface TabConfig {
  id: ArrestEditTab;
  label: string;
  icon?: React.ReactNode;
}

type ArrestEditableMasterNameFields = Partial<
  Pick<
    IMasterNamePerson,
    | 'suffix'
    | 'firstName'
    | 'middleName'
    | 'lastName'
    | 'maidenName'
    | 'aliasName'
    | 'nickName'
    | 'sex'
    | 'birthday'
    | 'placeOfBirth'
    | 'ssn'
    | 'nysid'
    | 'fbiNumber'
    | 'race'
    | 'ethnicOrigin'
    | 'education'
    | 'religion'
    | 'citizenOf'
    | 'maritalStatus'
    | 'nationality'
    | 'spokenLanguages'
    | 'gangAffiliations'
    | 'isVerified'
    | 'transient'
    | 'addresses'
    | 'phoneNumbers'
    | 'socialMedia'
    | 'emergencyContacts'
    | 'employerInfo'
    | 'physicalCharacteristics'
  >
>;

// Form data extending IArresteeForm with additional fields for signatures
export interface IArrestFormData
  extends IArresteeForm,
    ArrestEditableMasterNameFields {
  driverLicense?: Partial<IDriverLicense>;
  // Administrative info (read-only, not submitted)
  ori?: string;
  agencyName?: string;
  caseNumber?: string;
  arrestIdDisplay?: string;
  nysidNumber?: string;
  obtsNumber?: string;
  refNumber?: string;
  masterNameRefNumber?: string;
  fbiNumber?: string;
  division?: string;

  // Personal info (read-only from masterName)
  fullName?: string;
  dateOfBirth?: string;
  age?: number;
  ethnicity?: string;
  address?: string;
  phone?: string;
  driversLicense?: string;
  ssn_prev?: string;
  emails?: string[] | { email: string }[];
  masterNameFiles?: IFile[];

  // Arrest information
  arrestTime?: string;
  arrestLocation?: IAddress;
  juvenile?: string;
  assistingOfficer?: string;

  // Co-defendants and Miranda
  coDefendantsArrestNumber?: string[];
  miranda?: string;
  mirandaBy?: string;
  mirandaDate?: Date | null;
  mirandaTime?: string;

  // Statements and status
  statements?: string;

  // Warrant and ID
  searchWarrant?: string;
  idProcedure?: string;

  // Arraignment
  arraignmentCourt?: string;
  arraignmentJudge?: string;
  arraignmentDate?: Date | null;
  arraignmentTime?: string;

  // Evidence and property
  property?: string;
  evidence?: string;
  processedBy?: string;

  // Disposition
  disposition?: string;

  // Incident details
  incidentNumber?: string;
  bailAmount?: number;
  bondsman?: string;

  // Photo and warrant
  photoNumber?: string;
  warrantNumber?: string;

  // Other agency
  arrestForOtherAgency?: boolean;
  otherAgency?: string;
  fingerprintPictureTaken?: string;
  offenseAddress?: IAddress;

  // Return details
  offenseDate?: Date | null;
  returnCourt?: string;
  returnJudge?: string;
  returnDate?: Date | null;
  returnTime?: string;

  // Defendant transfer
  defendantCaseTurnedOverToAgency?: string;

  // Officer details
  officerName?: string;
  // Arrestee turned over details renamed from officerTime and officerDate
  arresteeTurnedOverTime?: string;
  arresteeTurnedOverDate?: Date | null;

  // Notes and files
  arrestNotes?: string;
  arrestFiles?: IFile[];

  // Arrestee Residence & Age Group
  arresteeResidence?: string;
  arresteeAgeGroup?: string;
  arresteeAge?: number;

  // Signatures (new fields - frontend only)
  arrestingOfficerSignature?: string;
  supervisorSignature?: string;

  // Verification
  safisSubmitted?: boolean;
}

// Props for individual tab components
export interface TabComponentProps {
  viewMode?: boolean;
  users?: IUser[];
  policeUsers?: IUser[];
}

// Associated person type for display
export interface IAssociatedPerson {
  _id: string;
  name: string;
  role: string; // witness, victim, etc.
  relationship?: string;
}

// Narrative type
export interface INarrativeItem {
  _id: string;
  title?: string;
  content: string;
  author?: IUser;
  createdAt: string;
}

// Helper to get default form values from arrestee
export const getDefaultFormValues = (
  arrestee?: IArrestee,
): Partial<IArrestFormData> => {
  if (!arrestee) {
    return {
      arresteeWeapons: [],
      safisSubmitted: false,
    };
  }

  // Extract time from arrestDate if it exists
  let arrestTime: string | undefined;
  if (arrestee.arrestDate) {
    const date = new Date(arrestee.arrestDate);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    arrestTime = `${hours}:${minutes}`;
  }

  // Extract times from other date fields
  let mirandaTime: string | undefined;
  if (arrestee.mirandaDate) {
    const date = new Date(arrestee.mirandaDate);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    mirandaTime = `${hours}:${minutes}`;
  }

  let arraignmentTime: string | undefined;
  if (arrestee.arraignmentDate) {
    const date = new Date(arrestee.arraignmentDate);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    arraignmentTime = `${hours}:${minutes}`;
  }

  let returnTime: string | undefined;
  if (arrestee.returnDate) {
    const date = new Date(arrestee.returnDate);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    returnTime = `${hours}:${minutes}`;
  }

  let arresteeTurnedOverTime: string | undefined;
  if (arrestee.arresteeTurnedOverDate) {
    const date = new Date(arrestee.arresteeTurnedOverDate);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    arresteeTurnedOverTime = `${hours}:${minutes}`;
  }

  const masterName =
    typeof arrestee.masterName === 'object' && arrestee.masterName
      ? (arrestee.masterName as IMasterNamePerson)
      : undefined;

  // DOB is the source of truth for Arrestee Age / Age Group whenever it is
  // present: derive both from the linked master-name birthday. Fall back to
  // stored values only when DOB is missing, which preserves manually-entered
  // "Over 98" cases without a known DOB.
  const computedAge = calculateAgeYears(masterName?.birthday);
  const derivedAgeGroup =
    computedAge !== null
      ? computedAge > 98
        ? AgeGroup.OVER_NINETY_EIGHT_YEARS_OLD
        : AgeGroup.EXACT_AGE
      : undefined;
  const derivedAge =
    computedAge !== null && computedAge <= 98 ? computedAge : undefined;

  return {
    // Administrative Information
    nysidNumber: masterName?.nysid ?? undefined,
    nysid: masterName?.nysid ?? undefined,
    obtsNumber: arrestee.obtsNumber ?? undefined,
    refNumber: arrestee.refNumber ?? undefined,
    masterNameRefNumber: masterName?.refNumber ?? undefined,
    fbiNumber: masterName?.fbiNumber ?? undefined,
    division: arrestee.division ?? undefined,

    // Linked master name fields editable from arrest form
    suffix: masterName?.suffix ?? undefined,
    firstName: masterName?.firstName ?? '',
    middleName: masterName?.middleName ?? undefined,
    lastName: masterName?.lastName ?? '',
    maidenName: masterName?.maidenName ?? undefined,
    aliasName: masterName?.aliasName ?? undefined,
    nickName: masterName?.nickName ?? undefined,
    sex: masterName?.sex ?? undefined,
    birthday: masterName?.birthday ?? undefined,
    placeOfBirth: masterName?.placeOfBirth ?? undefined,
    ssn: masterName?.ssn ?? undefined,
    ssn_prev: masterName?.ssn ?? '',
    race: masterName?.race ?? undefined,
    ethnicOrigin: masterName?.ethnicOrigin ?? undefined,
    education: masterName?.education ?? undefined,
    religion: masterName?.religion ?? undefined,
    // Legacy records may have `citizenOf: "Unknown"` which is not in the backend
    // Country enum; treat as empty so the PUT payload omits it instead of 400'ing.
    citizenOf:
      masterName?.citizenOf && masterName.citizenOf !== 'Unknown'
        ? masterName.citizenOf
        : undefined,
    maritalStatus: masterName?.maritalStatus ?? undefined,
    nationality: masterName?.nationality ?? undefined,
    gangAffiliations: masterName?.gangAffiliations ?? undefined,
    isVerified: masterName?.isVerified ?? false,
    spokenLanguages: masterName?.spokenLanguages ?? [],
    transient: masterName?.transient ?? false,
    masterNameFiles: masterName?.files || [],
    addresses: masterName?.addresses ?? [],
    phoneNumbers:
      masterName?.phoneNumbers?.map(({ type, phoneNumber }) => ({
        type,
        phoneNumber: parsePhoneNumber(phoneNumber),
      })) ?? [],
    socialMedia: masterName?.socialMedia ?? [],
    emails:
      masterName?.emails?.map((email) => ({
        email,
      })) ?? [],
    emergencyContacts: masterName?.emergencyContacts ?? [],
    employerInfo: masterName?.employerInfo
      ? {
          ...masterName.employerInfo,
          phoneNumber: parsePhoneNumber(masterName.employerInfo.phoneNumber),
        }
      : undefined,
    driverLicense: masterName?.driverLicense
      ? {
          ...masterName.driverLicense,
        }
      : undefined,
    physicalCharacteristics: masterName?.physicalCharacteristics ?? {
      others: [],
    },

    // Arrest Information - Basic
    arrestLocation:
      typeof arrestee.arrestLocation === 'object' && arrestee.arrestLocation
        ? arrestee.arrestLocation
        : undefined,
    arrestDate: arrestee.arrestDate,
    arrestTime,
    arrestType: arrestee.arrestType,
    arrestTypeCode: arrestee.arrestTypeCode,
    arrestStatus: arrestee.arrestStatus,
    arrestingOfficer: arrestee.arrestingOfficer?._id,
    bookingOfficer: arrestee.bookingOfficer?._id,
    condition: arrestee.condition,
    attitude: arrestee.attitude,
    medicalAttention: arrestee.medicalAttention,
    injury: arrestee.injury,
    drugsNarcotics: arrestee.drugsNarcotics,
    bloodAlcoholLevel: arrestee.bloodAlcoholLevel,
    juvenileReleaseStatus: arrestee.juvenileReleaseStatus,
    arresteeWeapons: arrestee.arresteeWeapons || [],
    arrestLarcenyType: arrestee.arrestLarcenyType,
    arresteeResidence: arrestee.arresteeResidence ?? undefined,
    arresteeAgeGroup: masterName
      ? derivedAgeGroup
      : arrestee.arresteeAgeGroup ?? undefined,
    arresteeAge: masterName ? derivedAge : arrestee.arresteeAge ?? undefined,
    notes: arrestee.notes,

    // Additional Officers
    assistingOfficer: arrestee.assistingOfficer?._id,

    juvenile:
      arrestee.juvenile ??
      (isArresteeJuvenile(arrestee.masterName as IMasterNamePerson)
        ? 'YES'
        : 'NO'),
    coDefendantsArrestNumber: arrestee.coDefendantsArrestNumber || [],

    // Miranda Rights
    miranda: arrestee.miranda ?? undefined,
    mirandaBy: arrestee.mirandaBy?._id,
    mirandaDate: arrestee.mirandaDate,
    mirandaTime,

    // Statements & Legal
    statements: arrestee.statements ?? undefined,
    courtStatus: arrestee.courtStatus ?? undefined,
    searchWarrant: arrestee.searchWarrant ?? undefined,
    idProcedure: arrestee.idProcedure ?? undefined,

    // Arraignment
    arraignmentCourt: arrestee.arraignmentCourt?._id,
    arraignmentJudge: arrestee.arraignmentJudge ?? undefined,
    arraignmentDate: arrestee.arraignmentDate,
    arraignmentTime,

    // Evidence & Property
    property: arrestee.property ?? undefined,
    evidence: arrestee.evidence ?? undefined,
    processedBy: arrestee.processedBy?._id,

    // Disposition & Status
    disposition: arrestee.disposition ?? undefined,
    incidentNumber: arrestee.incidentNumber?._id,
    bailAmount: arrestee.bailAmount ?? undefined,
    bondsman: arrestee.bondsman ?? undefined,

    // Photo & Warrant
    photoNumber: arrestee.photoNumber ?? undefined,
    warrantNumber: arrestee.warrantNumber ?? undefined,

    // Other Agency
    arrestForOtherAgency: arrestee.arrestForOtherAgency ?? undefined,
    otherAgency: arrestee.otherAgency?._id,
    fingerprintPictureTaken: arrestee.fingerprintPictureTaken ?? undefined,

    // Offense Details
    offenseAddress:
      typeof arrestee.offenseAddress === 'object' && arrestee.offenseAddress
        ? arrestee.offenseAddress
        : undefined,
    offenseDate: arrestee.offenseDate,

    // Return Court
    returnCourt: arrestee.returnCourt?._id,
    returnJudge: arrestee.returnJudge ?? undefined,
    returnDate: arrestee.returnDate,
    returnTime,

    // Transfer
    defendantCaseTurnedOverToAgency:
      arrestee.defendantCaseTurnedOverToAgency ?? undefined,

    // Officer Details
    officerName: arrestee.officerName?._id,
    arresteeTurnedOverDate: arrestee.arresteeTurnedOverDate,
    arresteeTurnedOverTime,

    // Notes
    arrestNotes: arrestee.arrestNotes ?? undefined,

    // Files
    arrestFiles: arrestee.files || [],

    // Employment - initialize from master name employer name only (checkbox is disabled)
    employed: Boolean((arrestee.masterName as any)?.employerInfo?.name?.trim()),

    // Signatures
    arrestingOfficerSignature: arrestee.arrestingOfficerSignature ?? undefined,
    supervisorSignature: arrestee.supervisorSignature ?? undefined,
    safisLatentPrintIdentificationArrest:
      arrestee.safisLatentPrintIdentificationArrest ?? undefined,

    // Display-only fields
    arrestIdDisplay: arrestee.arrestId || '',
    safisSubmitted: false,
  };
};

// Helper to check if arrestee is a juvenile based on age
export const isArresteeJuvenile = (masterName?: IMasterNamePerson): boolean => {
  if (!masterName?.birthday) return false;

  const dob = parseLocalDate(masterName.birthday);
  if (!dob) return false;
  const today = new Date();
  const age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    return age - 1 < 18;
  }

  return age < 18;
};
